import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import baseUrl from '../../../BaseURL';
import { ContextData } from '../../../context/Context';
import './PaymentResponse.css';

const money = value => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(value || 0);
const validReference = reference => /^FIN-WALLET-[a-f0-9-]{36}$/.test(reference || '');
export default function PaymentResponse() {
  const { reference } = useParams();
  const { accesstoken, setRefresh, setRefreshAmount } = useContext(ContextData);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const [retry, setRetry] = useState(0);
  const notified = useRef('');

  useEffect(() => {
    let active = true;
    let timer;
    const controller = new AbortController();
    setPayment(null); setError(''); setChecking(true);
    const config = { withCredentials: true, headers: { authorization: `Bearer ${accesstoken}` }, signal: controller.signal, timeout: 15000 };
    async function check() {
      let poll = false;
      try {
        const { data } = await axios.get(`${baseUrl}/auth/wallet/recharges/response/${encodeURIComponent(reference)}`, config);
        if (!active) return;
        if (data?.status !== true || data.reference !== reference || !['credited','validating','awaiting_payment','review','failed'].includes(data.paymentStatus)) throw new Error('Invalid payment response');
        setPayment(data); setError('');
        poll = ['validating','awaiting_payment'].includes(data.paymentStatus);
        if (data.paymentStatus === 'credited' && notified.current !== reference) {
          notified.current = reference;
          setRefresh(value => !value); setRefreshAmount(value => !value);
          window.dispatchEvent(new CustomEvent('fin-wallet-credited', { detail: { ...data, alreadyCredited: true } }));
        }
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || 'We could not confirm your payment status. Your reference is saved. Please check again; do not pay twice.');
        poll = ![400,401,403,404].includes(err.response?.status);
      } finally {
        if (active) { setChecking(false); if (poll) timer = setTimeout(check, 5000); }
      }
    }
    async function start() {
      if (!validReference(reference)) { setError('This payment link is invalid. Open purchase history to find your payment.'); setChecking(false); return; }
      // The browser only requests a check; the server verifies and credits the payment.
      try { await axios.put(`${baseUrl}/auth/user_amount`, { reference }, config); }
      catch { /* Read the owned payment status even if queuing is temporarily unavailable. */ }
      if (active) void check();
    }
    void start();
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [reference, accesstoken, retry, setRefresh, setRefreshAmount]);

  const status = payment?.paymentStatus;
  const credited = status === 'credited';
  const attention = ['review','failed'].includes(status);
  const title = credited ? 'Your wallet has been credited' : status === 'review' ? 'Your payment needs a review' : status === 'failed' ? 'Payment was not confirmed' : error && !payment ? 'Payment status unavailable' : 'Validating your payment';
  const description = credited ? `${Number(payment.quantity).toLocaleString()} registration credits have been added to your wallet. You can now continue registering members.`
    : status === 'review' ? 'The payment details need an additional check. Contact an administrator with the reference below. Please do not pay again.'
    : status === 'failed' ? 'Paystack has not confirmed a successful payment. If you were charged, check again or contact an administrator with your reference.'
    : 'Your account will be credited once the server confirms your payment. You can leave this page; validation continues in the background.';

  return <main className="fin-payment-response"><article className={`fin-payment-receipt${credited ? ' is-credited' : ''}`} aria-labelledby="fin-payment-title">
    <div className="fin-payment-brand">FITYANUL ISLAM OF NIGERIA<span>Registration wallet</span></div>
    <div className="fin-payment-result-icon">{credited ? <CheckCircleOutlineIcon /> : attention || error ? <InfoOutlinedIcon /> : <CircularProgress size={44} thickness={3} />}</div>
    <span className="fin-payment-kicker">{credited ? 'PAYMENT CONFIRMED' : attention ? 'PAYMENT UPDATE' : 'PAYMENT STATUS'}</span>
    <h1 id="fin-payment-title" role="status">{title}</h1><p className="fin-payment-description">{description}</p>
    {error && <Alert severity="warning" sx={{ marginBottom: 3, textAlign: 'left' }}>{error}</Alert>}
    <dl className="fin-payment-details"><div><dt>Payment reference</dt><dd className="fin-payment-reference">{reference || 'Unavailable'}</dd></div>{payment && <><div><dt>Registration credits</dt><dd>{Number(payment.quantity).toLocaleString()}</dd></div><div><dt>Recharge value</dt><dd>{money(payment.amount)}</dd></div>{credited && <div><dt>Available wallet credits</dt><dd>{Number(payment.balance).toLocaleString()}</dd></div>}</>}</dl>
    {payment && <p className="fin-payment-fee-note">Recharge value excludes any processing fee added by Paystack.</p>}
    <div className="fin-payment-response-actions"><Button component={Link} to="/dashboard/purchase-history" variant="contained">View purchase history</Button><Button component={Link} to="/dashboard/members" variant="outlined">Back to dashboard</Button>{!credited && validReference(reference) && <Button disabled={checking} onClick={() => setRetry(value => value + 1)}>{checking ? 'Checking payment…' : 'Check again'}</Button>}</div>
    <p className="fin-payment-footer">{credited ? 'Keep your payment reference for your records.' : 'Credits are added only after server confirmation.'}</p>
  </article></main>;
}
